import React, { useState } from 'react';
import { useForm, FormProvider } from "react-hook-form";
import { 
  Card, 
  CardContent,
  CardFooter
} from '@/components/ui/card';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { toast } from "@/hooks/use-toast";
import { ArrowRightIcon, RefreshCwIcon, SaveIcon } from 'lucide-react';
import GeneralDataForm from './GeneralDataForm';
import ServicesForm from './ServicesForm';
import RepatForm from './RepatForm';
import RatesForm from './RatesForm';
import SamplesForm from './SamplesForm';
import { RoleBasedElement } from '@/features/auth/components/RoleBasedElement';
import { ROLES } from '@/features/auth/constants/roles';

interface AccommodationFormProps {
  mode?: 'create' | 'edit' | 'view';
  defaultValues?: any;
}

const AccommodationForm = ({ mode = 'create', defaultValues = {} }: AccommodationFormProps) => {
  const [activeTab, setActiveTab] = useState('general');
  
  const methods = useForm({
    defaultValues: defaultValues
  });

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  // Function to handle the next tab navigation
  const handleNextTab = () => {
    const tabOrder = ['general', 'services', 'rates', 'repat', 'samples'];
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex < tabOrder.length - 1) {
      setActiveTab(tabOrder[currentIndex + 1]);
    }
  };

  // Function to handle form reset
  const handleReset = () => {
    methods.reset();
    toast({
      title: "Formulario restablecido",
      description: "Todos los campos han sido restablecidos a sus valores iniciales."
    });
  };

  // Function to handle form submission
  const handleSave = methods.handleSubmit((data) => {
    console.log('Form data:', data);
    toast({
      title: "Formulario guardado",
      description: "Los datos han sido guardados exitosamente."
    });
  });

  // Determine if form should be readonly based on mode
  const isReadOnly = mode === 'view';

  return (
    
      <FormProvider {...methods}>
        <form onSubmit={handleSave}>
          <Card className="w-full">
            <CardContent className="p-6">
              <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList className="grid w-full grid-cols-5 mb-8">
                  <TabsTrigger value="general">Datos Generales</TabsTrigger>
                  <TabsTrigger value="services">Servicios</TabsTrigger>
                  <TabsTrigger value="rates">Tarifas</TabsTrigger>
                  <TabsTrigger value="repat">Repat</TabsTrigger>
                  <TabsTrigger value="samples">Muestras</TabsTrigger>
                </TabsList>
                <TabsContent value="general">
                  <GeneralDataForm readOnly={isReadOnly} />
                </TabsContent>
                <TabsContent value="services">
                  <ServicesForm readOnly={isReadOnly} />
                </TabsContent>
                <TabsContent value="rates">
                  <RatesForm readOnly={isReadOnly} />
                </TabsContent>
                <TabsContent value="repat">
                  <RepatForm readOnly={isReadOnly} />
                </TabsContent>
                <TabsContent value="samples">
                  <SamplesForm readOnly={isReadOnly} />
                </TabsContent>
              </Tabs>
            </CardContent>
            <CardFooter className="flex justify-between border-t p-6 bg-gray-50">
              <div>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleReset}
                  disabled={isReadOnly}
                  className="gap-2"
                >
                  <RefreshCwIcon className="h-4 w-4" />
                  Restablecer
                </Button>
              </div>
              <RoleBasedElement 
      requiredRoles={[ROLES.ADMIN, ROLES.FISCALIZACION_EDITOR]}
      fallback={
        <Card className="w-full">
          <CardContent className="p-6">
            <div className="text-center text-red-500">
              No tienes permisos para acceder a este formulario.
            </div>
          </CardContent>
        </Card>
      }
    >
              <div className="flex gap-3">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={handleNextTab}
                  disabled={activeTab === 'samples' || isReadOnly}
                  className="gap-2"
                >
                  Siguiente
                  <ArrowRightIcon className="h-4 w-4" />
                </Button>
                <Button 
                  type="submit"
                  disabled={isReadOnly}
                  className="gap-2"
                >
                  <SaveIcon className="h-4 w-4" />
                  Guardar
                </Button>
              </div>
              </RoleBasedElement>
            </CardFooter>
          </Card>
        </form>
      </FormProvider>
    
  );
};

export default AccommodationForm;
